import {AfterViewInit, Component, Input, OnChanges, OnInit, SimpleChanges, ViewChild} from '@angular/core';
import {MatTableDataSource} from '@angular/material/table';
import {MatPaginator} from '@angular/material/paginator';
import {BehaviorSubject} from 'rxjs';
import {take} from 'rxjs/operators';
import {MatDialog} from '@angular/material/dialog';
import {NameValueDTO} from '../../../../../../common/models/NameValueDTO';
import {SharedService} from '../../../../../../common/services/shared-service';
import {ApplicationDTO} from '../../../../../models/application/ApplicationDTO';
import {ApplicationPersonDTO} from '../../../../../models/application/ApplicationPersonDTO';
import {ApplicationDocumentFileService} from '../../../../../services/application-document-file.service';
import {ApplicationDocumentDTO, DocumentTableDTO} from '../../../../../models/application/ApplicationDocumentDTO';
import {ConfirmationDialogComponent} from '../../../../../../common/dialogs/confirmation-dialog/confirmation-dialog.component';
import {ApplicationDocumentUploadDialogComponent} from '../../../../dialogs/application-document-upload-dialog/application-document-upload-dialog.component';

enum TableColumns {
  Person = 'person',
  DocumentType = 'documentType',
  ReferenceNumber = 'referenceNumber',
  Actions = 'actions'
}

@Component({
  selector: 'greydom-application-documents',
  templateUrl: './application-documents.component.html',
  styleUrls: []
})
export class ApplicationDocumentsComponent implements OnInit, OnChanges, AfterViewInit {
  readonly tableColumns = TableColumns;

  @Input() application: ApplicationDTO;

  pageSizeOptions: number[] = [5, 10, 25, 100];
  displayedColumns: string[] = Object.values(TableColumns);
  dataSource: MatTableDataSource<DocumentTableDTO>;
  persons: NameValueDTO[] = [];
  selectedPersonId: number | null = null;

  @ViewChild(MatPaginator) paginator: MatPaginator;

  private documentsSubject = new BehaviorSubject<DocumentTableDTO[]>([]);

  constructor(
    private dialog: MatDialog,
    private sharedService: SharedService,
    private applicationDocumentFileService: ApplicationDocumentFileService,
  ) {
    this.documentsSubject.subscribe(documents => {
      const filteredDocs = this.selectedPersonId
        ? documents.filter(doc =>
          doc.applicationPersonId === this.selectedPersonId ||
          (!doc.isPersonDocument && this.selectedPersonId === -1)
        )
        : documents;

      this.dataSource = new MatTableDataSource<DocumentTableDTO>(filteredDocs);
      if (this.paginator) {
        this.dataSource.paginator = this.paginator;
      }
    });
  }

  ngOnInit() {
    this.initializePersonsDDNList();
  }


  ngOnChanges(changes: SimpleChanges) {
    if (changes.application && changes.application.currentValue) {
      this.getDocumentsFromApplication();
    }
  }

  ngAfterViewInit(): void {
    if (this.dataSource && this.paginator) {
      this.dataSource.paginator = this.paginator;
    }
  }

  initializePersonsDDNList() {
    if (this.application?.membership?.persons) {
      this.persons = [
        {name: 'All Documents', value: null},
        {name: 'General Documents', value: -1}, // Info: General Documents are Membership level documents
        ...this.application.membership.persons.map(person => ({
          name: `${person.firstName} ${person.lastName}`,
          value: person.applicationPersonId
        }))
      ];
    }
  }

  onClickDownloadDocument(documentReferenceNumber: string): void {
    this.applicationDocumentFileService.downloadFileByReferenceNumber(documentReferenceNumber);
  }

  onDeleteDocument(document: DocumentTableDTO): void {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '40vw',
      data: `Are you sure you want to delete this document?`
    });

    dialogRef.afterClosed()
      .pipe(take(1))
      .subscribe(result => {
        if (result) {
          this.applicationDocumentFileService.deletePerDocumentWithFile(document)
            .pipe(take(1))
            .subscribe({
              next: () => {
                this.sharedService.openSnackBar('Document deleted successfully');
                // Update local state by filtering out deleted document
                const currentDocs = this.documentsSubject.getValue();
                this.documentsSubject.next(
                  currentDocs.filter(doc => doc.applicationDocumentId !== document.applicationDocumentId)
                );
              },
              error: (error) => {
                this.sharedService.openErrorDialog(error.message || 'Error deleting document');
              }
            });
        }
      });
  }

  openUploadDialog(): void {
    const dialogRef = this.dialog.open(ApplicationDocumentUploadDialogComponent, {
      width: '500px',
      data: {
        persons: this.application.membership.persons,
        applicationId: this.application.applicationId
      }
    });

    dialogRef.afterClosed()
      .pipe(take(1))
      .subscribe((result: ApplicationDocumentDTO & { person: ApplicationPersonDTO }) => {
        if (result) {
          // Add new document to local state
          const newDocument: DocumentTableDTO = {
            ...result,
            person: `${result.person.firstName} ${result.person.lastName}`,
            isPersonDocument: true,
            applicationPersonId: result.person.applicationPersonId
          };
          const currentDocs = this.documentsSubject.getValue();
          this.documentsSubject.next([...currentDocs, newDocument]);
        }
      });
  }


  onPersonFilterChange(person: NameValueDTO): void {
    this.selectedPersonId = person.value;
    this.documentsSubject.next(this.documentsSubject.getValue());
  }

  private getDocumentsFromApplication() {
    const documents: DocumentTableDTO[] = [];

    // Membership documents
    if (this.application.membership?.documents) {
      documents.push(...this.application.membership?.documents
        .map((document: ApplicationDocumentDTO) => ({
          ...document,
          person: 'General',
          isPersonDocument: false
        })));
    }

    // Dependent documents
    if (this.application.membership?.persons) {
      this.application.membership.persons
        .forEach((person: ApplicationPersonDTO) => {
          if (person.documents) {
            documents.push(...person.documents
              .map((personDocument: ApplicationDocumentDTO) => ({
                ...personDocument,
                person: `${person.firstName} ${person.lastName}`,
                isPersonDocument: true,
                applicationPersonId: person.applicationPersonId
              })));
          }
        });
    }

    this.documentsSubject.next(documents);
  }
}
